<template>
  <div class="event-highlights-grid">
    <GalleryThumbnail
      v-if="!loading"
      v-for="highlight in firstHighlights"
      :key="highlight.id"
      :image="highlight.event_medium.uploaded_file.thumb.url"
      @click="openLightbox(highlight.index)"
      style="cursor: pointer"
      :size="0"
    />
    <GalleryViewSkeletonLoader
      v-if="loading"
      v-for="i in loaderCount"
      :key="i"
    ></GalleryViewSkeletonLoader>
    <div>
      <GalleryShowMore
        style="height: 100%"
        :size="0"
        :total-elements="highlightsCount"
        v-if="!loading"
        @click="openGalleryViewEvent.emit(true)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, ComputedRef, defineComponent, inject, PropType } from "vue"
import GalleryViewSkeletonLoader from "features/gallery/view/GalleryViewSkeletonLoader.vue"
import { Highlight } from "suus-api"
import GalleryThumbnail from "features/gallery/thumbnail/GalleryThumbnail.vue"
import { Event } from "suus-api"
import { GalleryService } from "features/gallery/service/gallery.service"
import EventHighlightsView from "features/events/highlights/EventHighlightsView.vue"
import GalleryShowMore from "features/gallery/GalleryShowMore.vue"
import { SimpleEventBus } from "core/event-bus/event-bus"
import { EventLightboxService } from "features/events/highlights/lightbox/event-lightbox.service"

const SHOWN_HIGHLIGHTS_COUNT = 9

export default defineComponent({
  name: "EventHighlightsGrid",
  components: {
    GalleryShowMore,
    EventHighlightsView,
    GalleryThumbnail,
    GalleryViewSkeletonLoader,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    highlights: {
      type: Array as PropType<Highlight[]>,
      default: () => [],
    },
    event: {
      type: Object as PropType<Event>,
      required: true,
    },
    openGalleryViewEvent: {
      type: Object as PropType<SimpleEventBus<boolean>>,
      default: null,
    },
  },
  setup(props) {
    const galleryService: GalleryService = inject("GalleryService")
    const eventLightboxService: EventLightboxService = inject(
      "EventLightboxService"
    )
    const highlightsCount = galleryService.highlightsCount
    const highlights = computed(() => props.highlights)

    const firstHighlights: ComputedRef<Highlight[]> = computed(() => {
      return highlights.value.slice(0, SHOWN_HIGHLIGHTS_COUNT - 1)
    })

    const loaderCount = computed(() => {
      return Math.max(
        Math.min(SHOWN_HIGHLIGHTS_COUNT, highlightsCount.value - 1),
        0
      )
    })

    const openLightbox = (index: number) => {
      eventLightboxService.openLightbox(index)
    }

    return {
      firstHighlights,
      openLightbox,
      loaderCount,
      highlightsCount,
    }
  },
})
</script>

<style lang="sass" scoped>
$columns: 3
$rows: 3
.event-highlights-grid
  display: grid
  grid-template-columns: repeat($columns, 1fr)
  grid-gap: 8px
  grid-template-rows: repeat($rows, 1fr)
</style>
